/**
 * SupportTicketPage (Обращение) — a single support ticket's thread (ТЗ §8).
 *
 * Shows the ticket number, topic and RU status, the message thread (customer right /
 * support left) and a composer. The thread is kept fresh by polling the ticket detail
 * endpoint (the support channel has no WebSocket); a closed ticket locks the composer.
 */
import { useEffect, useRef, useState } from "react";

import { Badge, Button, Sheet } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import {
  SUPPORT_STATUS_PRESENTATION,
  closeTicket,
  isTicketClosed,
  postTicketMessage,
  useTicket,
  type SupportMessage,
} from "@entities/support";
import { ApiError } from "@shared/api";
import { StatePanel } from "@widgets/catalog-browser";
import { routes, useNavigate } from "@shared/router";

import styles from "./SupportTicketPage.module.css";

/** How often the thread is re-fetched while the screen is open (ms). */
const POLL_INTERVAL_MS = 5000;

export interface SupportTicketPageProps {
  readonly ticketId: number;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { readonly detail?: unknown } | null;
    if (
      payload !== null &&
      typeof payload === "object" &&
      typeof payload.detail === "string"
    ) {
      return payload.detail;
    }
  }
  return "Не удалось отправить сообщение. Попробуйте ещё раз.";
}

export function SupportTicketPage({
  ticketId,
}: SupportTicketPageProps): JSX.Element {
  const navigate = useNavigate();
  const { data: ticket, isLoading, error, refetch } = useTicket(ticketId);

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  useBackButton(() => {
    navigate(routes.support());
  });

  // Poll the thread while the screen is open (no WebSocket for support).
  useEffect(() => {
    const id = window.setInterval(refetch, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [refetch]);

  const messageCount = ticket?.messages.length ?? 0;
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messageCount]);

  if (isLoading && !ticket) {
    return <StatePanel kind="loading" />;
  }
  if (error || !ticket) {
    return (
      <StatePanel
        kind="error"
        message="Обращение не найдено или недоступно."
        onRetry={refetch}
      />
    );
  }

  const status = SUPPORT_STATUS_PRESENTATION[ticket.status];
  const closed = isTicketClosed(ticket.status);

  const onSubmit = (): void => {
    const text = draft.trim();
    if (text.length === 0 || closed || sending) {
      return;
    }
    setSending(true);
    setSendError(null);
    postTicketMessage(ticket.id, text)
      .then(() => {
        setDraft("");
        refetch();
      })
      .catch((err: unknown) => {
        setSendError(errorText(err));
      })
      .finally(() => {
        setSending(false);
      });
  };

  const onConfirmClose = (): void => {
    if (closing) {
      return;
    }
    setClosing(true);
    setCloseError(null);
    closeTicket(ticket.id)
      .then(() => {
        setConfirmClose(false);
        refetch();
      })
      .catch((err: unknown) => {
        setCloseError(errorText(err));
      })
      .finally(() => {
        setClosing(false);
      });
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.back}
          aria-label="Назад"
          onClick={() => navigate(routes.support())}
        >
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
        </button>
        <div className={styles.headMain}>
          <h1 className={styles.number}>Обращение {ticket.number}</h1>
          <span className={styles.category}>{ticket.category_display}</span>
        </div>
        <Badge tone={status.tone}>{status.label}</Badge>
      </header>

      {!closed ? (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.closeTicket}
            onClick={() => {
              setCloseError(null);
              setConfirmClose(true);
            }}
          >
            Закрыть обращение
          </button>
        </div>
      ) : null}

      <div className={styles.log} role="log" aria-live="polite">
        {ticket.messages.length === 0 ? (
          <p className={styles.muted}>Сообщений пока нет.</p>
        ) : (
          ticket.messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className={styles.panel}>
        {closed ? (
          <p className={styles.lockedNote} role="status">
            Обращение закрыто. Чтобы продолжить, создайте новое.
          </p>
        ) : null}

        {sendError !== null ? (
          <p className={styles.errorNote} role="alert">
            {sendError}
          </p>
        ) : null}

        <div className={styles.composer}>
          <input
            className={styles.input}
            type="text"
            placeholder={closed ? "Обращение закрыто" : "Сообщение в поддержку…"}
            value={draft}
            maxLength={4000}
            disabled={closed}
            aria-label="Сообщение в поддержку"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
          />
          <button
            type="button"
            className={styles.send}
            aria-label="Отправить"
            disabled={closed || sending || draft.trim().length === 0}
            onClick={onSubmit}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M22 2 11 13" />
              <path d="m22 2-7 20-4-9-9-4z" />
            </svg>
          </button>
        </div>
      </div>

      <Sheet
        open={confirmClose}
        onClose={() => {
          if (!closing) {
            setConfirmClose(false);
          }
        }}
        title="Закрыть обращение"
      >
        <div className={styles.confirmBody}>
          <p className={styles.confirmText}>
            Вы уверены? Обращение будет закрыто. Если останутся вопросы, можно
            создать новое.
          </p>
          {closeError !== null ? (
            <p className={styles.errorNote} role="alert">
              {closeError}
            </p>
          ) : null}
          <div className={styles.confirmActions}>
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              disabled={closing}
              onClick={() => setConfirmClose(false)}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={closing}
              onClick={onConfirmClose}
            >
              {closing ? "Закрываем…" : "Закрыть"}
            </Button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}

interface MessageBubbleProps {
  readonly message: SupportMessage;
}

function MessageBubble({ message }: MessageBubbleProps): JSX.Element {
  const own = message.sender_role === "customer";
  const time = formatTime(message.created_at);
  if (own) {
    return (
      <div className={styles.rowOwn}>
        <div className={styles.bubbleWrapOwn}>
          <div className={styles.bubbleOwn}>
            {message.text ? (
              <span className={styles.bubbleText}>{message.text}</span>
            ) : null}
            {message.attachment_url ? (
              <a
                className={styles.attachment}
                href={message.attachment_url}
                target="_blank"
                rel="noreferrer"
              >
                Вложение
              </a>
            ) : null}
          </div>
          <span className={styles.caption}>Вы · {time}</span>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.rowStaff}>
      <span className={styles.avatar} aria-hidden="true">
        M
      </span>
      <div className={styles.bubbleWrap}>
        <div className={styles.bubbleStaff}>
          {message.text ? (
            <span className={styles.bubbleText}>{message.text}</span>
          ) : null}
          {message.attachment_url ? (
            <a
              className={styles.attachment}
              href={message.attachment_url}
              target="_blank"
              rel="noreferrer"
            >
              Вложение
            </a>
          ) : null}
        </div>
        <span className={styles.caption}>
          {message.sender_display_name || "Поддержка"} · {time}
        </span>
      </div>
    </div>
  );
}
